(() => {
  const controllerName = 'KohreeProductStickyNavigation';
  const existingController = window[controllerName];

  if (existingController?.initAll) {
    existingController.initAll();
    return;
  }

  const instances = new WeakMap();
  const navSelector = '[data-kohree-product-anchor-nav]';

  const getDocumentTop = (element) => {
    let top = 0;
    let current = element;

    while (current) {
      top += current.offsetTop || 0;
      current = current.offsetParent;
    }

    return top;
  };

  const initNav = (nav) => {
    if (!(nav instanceof HTMLElement)) return;

    const existingInstance = instances.get(nav);
    if (existingInstance) {
      existingInstance.refresh();
      return;
    }

    const sectionRoot = nav.closest('[data-kohree-product-anchor-nav-section]');
    const sectionWrapper = nav.closest('.shopify-section') || sectionRoot;
    const list = nav.querySelector('[data-kohree-product-anchor-list]');
    const linkElements = Array.from(nav.querySelectorAll('[data-kohree-product-anchor-link]'));

    if (!sectionRoot || !sectionWrapper || !list || !linkElements.length) return;

    const eventController = new AbortController();
    const targets = new Map();
    const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const normalize = (value) => (value || '').trim().toLowerCase();
    const autoHide = nav.dataset.kohreeAutoHide === 'true';
    const enableSticky = nav.dataset.kohreeEnableSticky === 'true';
    const topOffset = Number.parseFloat(nav.dataset.kohreeTopOffset || '0') || 0;
    let validLinks = [];
    let headerBoundary = 0;
    let stateFrame = null;
    let lockedAnchorId = '';
    let lockedUntil = 0;
    let resizeObserver = null;

    const matchesLabel = (value, target) => {
      const normalizedValue = normalize(value);
      const normalizedTarget = normalize(target);
      return normalizedValue === normalizedTarget || normalizedValue.startsWith(`${normalizedTarget} (`);
    };

    const getLinkForTarget = (targetId) => {
      const candidates = validLinks.filter((link) => link.dataset.kohreeAnchor === targetId);
      if (candidates.length < 2) return candidates[0];

      const activeTab = Array.from(document.querySelectorAll('[data-tabtitle]')).find((tab) =>
        tab.classList.contains('tab-enabled')
      );
      const activeTabTitle = activeTab?.dataset.tabtitle;

      return (
        candidates.find(
          (link) => link.dataset.kohreeTabLabel && matchesLabel(activeTabTitle, link.dataset.kohreeTabLabel)
        ) || candidates[0]
      );
    };

    const updateListAlignment = () => {
      list.dataset.kohreeOverflow = String(list.scrollWidth > list.clientWidth + 1);
    };

    const keepLinkVisible = (link) => {
      if (!window.matchMedia('(max-width: 767px)').matches) return;

      const listRect = list.getBoundingClientRect();
      const linkRect = link.getBoundingClientRect();
      const edge = 20;
      let horizontalOffset = 0;

      if (linkRect.left < listRect.left + edge) {
        horizontalOffset = linkRect.left - listRect.left - edge;
      } else if (linkRect.right > listRect.right - edge) {
        horizontalOffset = linkRect.right - listRect.right + edge;
      }

      if (horizontalOffset) {
        list.scrollBy({
          left: horizontalOffset,
          behavior: prefersReducedMotion() ? 'auto' : 'smooth'
        });
      }
    };

    const setActive = (link, moveIntoView = false) => {
      if (!link) return;

      const hasChanged = !link.classList.contains('is-active');

      linkElements.forEach((candidate) => {
        const isActive = candidate === link;
        candidate.classList.toggle('is-active', isActive);

        if (isActive) candidate.setAttribute('aria-current', 'location');
        else candidate.removeAttribute('aria-current');
      });

      if (moveIntoView && hasChanged) keepLinkVisible(link);
    };

    const measureHeaderBoundary = () => {
      const header = document.getElementById('x-header-container');
      headerBoundary = header ? getDocumentTop(header) + header.offsetHeight : 0;
    };

    const updateNavVisibility = () => {
      if (!enableSticky) {
        nav.dataset.kohreeNavVisible = 'true';
        return;
      }

      const hasPassedHeader = window.scrollY + topOffset > headerBoundary + 1;
      nav.dataset.kohreeNavVisible = String(hasPassedHeader);
    };

    const updateStickyState = () => {
      updateNavVisibility();

      if (!enableSticky) {
        nav.dataset.kohreeStuck = 'false';
        return;
      }

      const wrapperRect = sectionWrapper.getBoundingClientRect();
      nav.dataset.kohreeStuck = String(wrapperRect.top <= topOffset + 1 && window.scrollY > headerBoundary);
    };

    const updateActiveFromScroll = () => {
      if (nav.dataset.kohreeScrollSpy !== 'true' || !validLinks.length) return;

      if (lockedAnchorId && performance.now() < lockedUntil) {
        setActive(getLinkForTarget(lockedAnchorId), true);
        return;
      }

      lockedAnchorId = '';
      const referencePosition = window.scrollY + topOffset + (enableSticky ? nav.offsetHeight : 0) + 12;
      const targetPositions = [];
      const inspectedTargets = new Set();

      validLinks.forEach((link) => {
        const anchorId = link.dataset.kohreeAnchor;
        const target = anchorId ? targets.get(anchorId) : null;

        if (!anchorId || !target || inspectedTargets.has(anchorId)) return;
        inspectedTargets.add(anchorId);
        targetPositions.push({ anchorId, top: getDocumentTop(target) });
      });

      targetPositions.sort((first, second) => first.top - second.top);

      let activeAnchorId = targetPositions[0]?.anchorId || '';
      targetPositions.forEach((targetPosition) => {
        if (targetPosition.top <= referencePosition) activeAnchorId = targetPosition.anchorId;
      });

      setActive(getLinkForTarget(activeAnchorId), true);
    };

    const updateState = () => {
      updateStickyState();
      updateActiveFromScroll();
    };

    const scheduleStateUpdate = () => {
      if (stateFrame !== null) return;

      stateFrame = window.requestAnimationFrame(() => {
        stateFrame = null;
        updateState();
      });
    };

    const activateTab = (tabLabel) => {
      if (!tabLabel) return;

      const tab = Array.from(document.querySelectorAll('[data-tabtitle]')).find((candidate) =>
        matchesLabel(candidate.dataset.tabtitle, tabLabel)
      );

      if (tab && !tab.classList.contains('tab-enabled')) tab.click();
    };

    const scrollToTarget = (link, updateHistory) => {
      const anchorId = link?.dataset.kohreeAnchor;
      const target = anchorId ? targets.get(anchorId) : null;
      if (!anchorId || !target) return;

      activateTab(link.dataset.kohreeTabLabel);
      lockedAnchorId = anchorId;
      lockedUntil = performance.now() + 1200;
      setActive(link, true);

      const navHeight = enableSticky ? nav.offsetHeight : 0;
      const destination = Math.max(0, getDocumentTop(target) - topOffset - navHeight - 12);

      window.scrollTo({
        top: destination,
        behavior: nav.dataset.kohreeSmoothScroll === 'true' && !prefersReducedMotion() ? 'smooth' : 'auto'
      });

      if (updateHistory) window.history.replaceState(null, '', `#${anchorId}`);
      window.setTimeout(scheduleStateUpdate, 1250);
    };

    const syncActiveFromHash = (adjustScroll = false) => {
      let anchorId = '';

      try {
        anchorId = decodeURIComponent(window.location.hash.replace(/^#/, ''));
      } catch (_error) {
        anchorId = window.location.hash.replace(/^#/, '');
      }

      const link = anchorId && targets.has(anchorId) ? getLinkForTarget(anchorId) : null;
      if (!link) return false;

      lockedAnchorId = anchorId;
      lockedUntil = performance.now() + 1200;
      setActive(link, true);

      if (adjustScroll) {
        window.requestAnimationFrame(() => scrollToTarget(link, false));
      }

      return true;
    };

    const refresh = () => {
      targets.clear();
      validLinks = [];
      sectionRoot.hidden = false;

      linkElements.forEach((link) => {
        const anchorId = link.dataset.kohreeAnchor;
        const target = anchorId ? document.getElementById(anchorId) : null;
        const item = link.parentElement;

        if (item) item.hidden = Boolean(autoHide && !target);
        if (!anchorId || !target) return;

        targets.set(anchorId, target);
        validLinks.push(link);
      });

      if (!validLinks.length) {
        sectionRoot.hidden = true;
        nav.dataset.kohreeNavVisible = 'false';
        nav.dataset.kohreeStuck = 'false';
        return;
      }

      measureHeaderBoundary();
      updateListAlignment();

      if (!syncActiveFromHash(false)) updateActiveFromScroll();
      updateStickyState();
    };

    const destroy = () => {
      if (stateFrame !== null) window.cancelAnimationFrame(stateFrame);
      resizeObserver?.disconnect();
      eventController.abort();
      instances.delete(nav);
    };

    linkElements.forEach((link) => {
      link.addEventListener(
        'click',
        (event) => {
          if (!targets.has(link.dataset.kohreeAnchor)) return;
          event.preventDefault();
          scrollToTarget(link, true);
        },
        { signal: eventController.signal }
      );
    });

    window.addEventListener('scroll', scheduleStateUpdate, {
      passive: true,
      signal: eventController.signal
    });
    window.addEventListener(
      'resize',
      () => {
        measureHeaderBoundary();
        updateListAlignment();
        scheduleStateUpdate();
      },
      { signal: eventController.signal }
    );
    window.addEventListener('hashchange', () => syncActiveFromHash(false), {
      signal: eventController.signal
    });
    window.addEventListener('load', refresh, {
      once: true,
      signal: eventController.signal
    });

    if ('ResizeObserver' in window) {
      resizeObserver = new ResizeObserver(() => {
        measureHeaderBoundary();
        updateListAlignment();
        scheduleStateUpdate();
      });
      resizeObserver.observe(list);

      const header = document.getElementById('x-header-container');
      if (header) resizeObserver.observe(header);
    }

    instances.set(nav, { destroy, refresh });
    refresh();
    syncActiveFromHash(true);
    scheduleStateUpdate();
  };

  const initAll = (scope = document) => {
    if (scope instanceof Element && scope.matches(navSelector)) initNav(scope);
    scope.querySelectorAll?.(navSelector).forEach(initNav);
  };

  const destroyAll = (scope) => {
    const navs = [];

    if (scope instanceof Element && scope.matches(navSelector)) navs.push(scope);
    scope.querySelectorAll?.(navSelector).forEach((nav) => navs.push(nav));

    navs.forEach((nav) => instances.get(nav)?.destroy());
  };

  window[controllerName] = { initAll };
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroyAll(event.target));
  initAll();
})();
